import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "../../hooks/useTranslation";

export type StoryMode = "camera" | "text" | "gallery" | "canvas";

interface StoryControlsProps {
  mode: StoryMode;
  onModeChange: (mode: StoryMode) => void;
  onClose: () => void;
  onUpload: () => void;
  uploading: boolean;
  disabled?: boolean;
}

/** Instagram-style minimal top bar: close, mode pill, Next. */
const StoryControls: React.FC<StoryControlsProps> = ({
  mode,
  onModeChange,
  onClose,
  onUpload,
  uploading,
  disabled = false,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3 pt-3 pb-2"
      style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}
    >
      {/* Close - left */}
      <button
        type="button"
        onClick={onClose}
        disabled={uploading || disabled}
        className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white disabled:opacity-50 active:scale-95 transition-transform touch-manipulation"
        aria-label={t("story.close")}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Mode pill - center (minimal, no heavy borders) */}
      <div className="flex rounded-full bg-white/10 backdrop-blur-md p-0.5">
        <button
          type="button"
          onClick={() => onModeChange("camera")}
          disabled={disabled}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 touch-manipulation ${
            mode === "camera"
              ? "bg-white text-black shadow-sm"
              : "text-white/90 hover:text-white"
          }`}
        >
          {t("story.camera")}
        </button>
        <button
          type="button"
          onClick={() => onModeChange("canvas")}
          disabled={disabled}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 touch-manipulation ${
            mode === "canvas"
              ? "bg-white text-black shadow-sm"
              : "text-white/90 hover:text-white"
          }`}
        >
          {t("story.storyMode")}
        </button>
      </div>

      {/* Next / Share - right */}
      <button
        type="button"
        onClick={onUpload}
        disabled={uploading || disabled}
        className="min-w-[56px] h-10 px-4 rounded-full bg-white text-black text-sm font-semibold disabled:opacity-50 active:scale-95 transition-transform touch-manipulation flex items-center justify-center gap-1.5"
      >
        {uploading ? (
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-4 h-4 border-2 border-black border-t-transparent rounded-full"
          />
        ) : (
          t("story.publish")
        )}
      </button>
    </div>
  );
};

export default StoryControls;
